import React, { useState, useRef, useEffect } from 'react';
import './CameraManager.css';

const CameraManager = ({ camera, controls, onMessage }) => {
  const [cameraPosition, setCameraPosition] = useState({ x: 0, y: 0, z: 0 });
  const [targetPosition, setTargetPosition] = useState({ x: 0, y: 0, z: 0 });
  const [isExpanded, setIsExpanded] = useState(true);
  const positionInputRef = useRef({});
  const targetInputRef = useRef({});

  // Update local state when camera or controls change
  useEffect(() => {
    if (camera) {
      setCameraPosition({
        x: Math.round(camera.position.x),
        y: Math.round(camera.position.y),
        z: Math.round(camera.position.z)
      });
    }
    
    if (controls) {
      setTargetPosition({
        x: Math.round(controls.target.x),
        y: Math.round(controls.target.y),
        z: Math.round(controls.target.z)
      });
    }
  }, [camera, controls]);

  const handleCameraPositionChange = (axis, value) => {
    const numValue = Number(value);
    if (isNaN(numValue)) return;

    const newPosition = { ...cameraPosition, [axis]: numValue };
    setCameraPosition(newPosition);

    if (camera) {
      camera.position[axis] = numValue;
      if (controls) {
        controls.update();
      }
    }
  };

  const handleTargetPositionChange = (axis, value) => {
    const numValue = Number(value);
    if (isNaN(numValue)) return;

    const newTarget = { ...targetPosition, [axis]: numValue };
    setTargetPosition(newTarget);

    if (controls) {
      controls.target[axis] = numValue;
      controls.update();
    }
  };

  const resetCamera = () => {
    if (camera && controls) {
      camera.position.set(5000, 5000, 9000);
      controls.target.set(0, 0, 0);
      controls.update();
      
      setCameraPosition({ x: 5000, y: 5000, z: 9000 });
      setTargetPosition({ x: 0, y: 0, z: 0 });
      
      onMessage('Camera reset to default position', 'info');
    }
  };

  const focusOnOrigin = () => {
    if (camera && controls) {
      camera.position.set(1000, 1000, 1000);
      controls.target.set(0, 0, 0);
      controls.update();
      
      setCameraPosition({ x: 1000, y: 1000, z: 1000 });
      setTargetPosition({ x: 0, y: 0, z: 0 });
      
      onMessage('Camera focused on origin', 'info');
    }
  };

  const topView = () => {
    if (camera && controls) {
      camera.position.set(3000, 6000, 3000);
      controls.target.set(1000, 0, 3000);
      controls.update();
      
      setCameraPosition({ x: 3000, y: 6000, z: 3000 });
      setTargetPosition({ x: 1000, y: 0, z: 3000 });
      
      onMessage('Top view activated', 'info');
    }
  };

  const frontView = () => {
    if (camera && controls) {
      camera.position.set(1000, 1000, 14000);
      controls.target.set(0, 0, 0);
      controls.update();
      
      setCameraPosition({ x: 1000, y: 1000, z: 14000 });
      setTargetPosition({ x: 0, y: 0, z: 0 });
      
      onMessage('Front view activated', 'info');
    }
  };

  const sideView = () => {
    if (camera && controls) {
      camera.position.set(6000, 3000, 3000);
      controls.target.set(0, 1000, 3000);
      controls.update();
      
      setCameraPosition({ x: 6000, y: 3000, z: 3000 });
      setTargetPosition({ x: 0, y: 1000, z: 3000 });
      
      onMessage('Side view activated', 'info');
    }
  };


  const moveCamera = (direction) => {
    if (!camera || !controls) return;

    const moveDistance = 500;
    const panDistance = 250;

    switch (direction) {
      case 'up':
        if (window.event?.shiftKey) {
          // Move camera up
          camera.position.y += moveDistance;
        } else {
          // Pan up
          controls.target.y += panDistance;
          camera.position.y += panDistance;
        }
        break;
      case 'down':
        if (window.event?.shiftKey) {
          // Move camera down
          camera.position.y -= moveDistance;
        } else {
          // Pan down
          controls.target.y -= panDistance;
          camera.position.y -= panDistance;
        }
        break;
      case 'left':
        if (window.event?.shiftKey) {
          // Strafe left
          camera.position.x -= moveDistance;
        } else {
          // Pan left
          controls.target.x -= panDistance;
          camera.position.x -= panDistance;
        }
        break;
      case 'right':
        if (window.event?.shiftKey) {
          // Strafe right
          camera.position.x += moveDistance;
        } else {
          // Pan right
          controls.target.x += panDistance;
          camera.position.x += panDistance;
        }
        break;
      case 'forward':
        camera.translateZ(-moveDistance);
        break;
      case 'backward':
        camera.translateZ(moveDistance);
        break;
    }

    controls.update();
    
    // Update local state
    setCameraPosition({
      x: Math.round(camera.position.x),
      y: Math.round(camera.position.y),
      z: Math.round(camera.position.z)
    });
    setTargetPosition({
      x: Math.round(controls.target.x),
      y: Math.round(controls.target.y),
      z: Math.round(controls.target.z)
    });
  };

  if (!camera || !controls) {
    return (
      <div className="camera-manager">
        <div className="camera-unavailable">
          Camera controls not available
        </div>
      </div>
    );
  }

  return (
    <div className="camera-manager">
      <div className="camera-section">
        <div className="camera-header">
          <h3>Camera Manager</h3>
          <button 
            className={`expand-btn ${isExpanded ? 'expanded' : ''}`}
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? '▼' : '►'}
          </button>
        </div>

        {isExpanded && (
          <div className="camera-content">
            {/* Quick View Buttons 
            <div className="view-buttons">
              
         
              <button onClick={focusOnOrigin} className="btn-view" title="Focus on Origin">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <circle cx="12" cy="12" r="10" strokeWidth="2"/>
                  <circle cx="12" cy="12" r="2" strokeWidth="2"/>
                </svg>
                Origin
              </button>
             
            </div>  */}

            {/* Movement Controls  */}
            <div className="movement-controls">
              <div className="view-buttons-left">

                <button 
                  onClick={topView} 
                  className={`btn-view btn-view-modern`}
                  title="Top View (Y-axis)">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M4 4h16M4 4v16M4 20h16M20 4v16" strokeWidth="2"/>
                    <circle cx="12" cy="12" r="1.5" fill="currentColor"/>
                  </svg>                  
                </button>
                <button 
                  onClick={resetCamera} 
                  className="btn-view btn-reset-modern" 
                  title="Reset Camera">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <circle cx="12" cy="12" r="3" strokeWidth="2"/>
                    <path d="M12 6V3M12 21v-3M3 12h3M21 12h-3" strokeWidth="2"/>
                    <circle cx="12" cy="12" r="1.5" fill="currentColor"/>
                  </svg>                  
                </button>  

              </div>
              <div className="control-grid">
                <div></div>
                <button onClick={() => moveCamera('up')} className={`btn-move btn-move-modern`} title="Move Up (Shift: Pan)">
                  ↑
                </button>
                <div></div>
                <button onClick={() => moveCamera('left')} className={`btn-move btn-move-modern`} title="Move Left (Shift: Pan)">
                  ←
                </button>
                <button onClick={() => moveCamera('forward')} className={`btn-move btn-move-modern`} title="Move Forward">
                  ↗
                </button>
                <button onClick={() => moveCamera('right')} className={`btn-move btn-move-modern`} title="Move Right (Shift: Pan)">
                  →
                </button>
                <div></div>
                <button onClick={() => moveCamera('down')} className={`btn-move btn-move-modern`} title="Move Down (Shift: Pan)">
                  ↓
                </button>
                <button onClick={() => moveCamera('backward')} className={`btn-move btn-move-modern`} title="Move Backward">
                  ↙
                </button>
              </div>
              <div className="view-buttons-right">

              <button 
                onClick={frontView} 
                className={`btn-view btn-view-modern`}
                title="Front View (Z-axis)">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M4 4h16M4 20h16M4 4v16M20 4v16" strokeWidth="2"/>
                  <path d="M12 12l6 6M12 12l-6 6" strokeWidth="2"/>
                  <circle cx="12" cy="12" r="1.5" fill="currentColor"/>
                </svg>              
              </button>
              <button
                onClick={sideView} 
                className={`btn-view btn-view-modern`} 
                title="Side View (X-axis)">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M4 4v16M20 4v16M4 20h16" strokeWidth="2"/>
                  <circle cx="16" cy="12" r="1.5" fill="currentColor"/>
                  <circle cx="12" cy="12" r="1.5" fill="currentColor"/>
                  <circle cx="8" cy="12" r="1.5" fill="currentColor"/>
                </svg>
              </button>
              </div> 
            </div>  

            {/* Camera Position 
            <div className="position-section">
              <h4>Camera Position</h4>
              <div className="position-inputs">
                {['x', 'y', 'z'].map(axis => (
                  <div key={axis} className="input-group">
                    <label>{axis.toUpperCase()}</label>
                    <input
                      type="number"
                      value={cameraPosition[axis]}
                      onChange={(e) => handleCameraPositionChange(axis, e.target.value)}
                      ref={el => positionInputRef.current[axis] = el}
                      className="coord-input"
                    />
                  </div>
                ))}
              </div>
            </div>  */}

            {/* Target Position 
            <div className="position-section">
              <h4>Look At Target</h4>
              <div className="position-inputs">
                {['x', 'y', 'z'].map(axis => (
                  <div key={axis} className="input-group">
                    <label>{axis.toUpperCase()}</label>
                    <input
                      type="number"
                      value={targetPosition[axis]}
                      onChange={(e) => handleTargetPositionChange(axis, e.target.value)}
                      ref={el => targetInputRef.current[axis] = el}
                      className="coord-input"
                    />
                  </div>
                ))}
              </div>
            </div> */}

            {/* Keyboard Shortcuts Help 
            <div className="shortcuts-help">
              <h4>Keyboard Shortcuts</h4>
              <div className="shortcuts-grid">
                <div>W/S</div><div>Forward/Backward</div>
                <div>A/D</div><div>Strafe Left/Right</div>
                <div>Q/E</div><div>Move Up/Down</div>
                <div>R</div><div>Reset Camera</div>
                <div>Arrows</div><div>Pan Camera</div>
                <div>Shift+Arrows</div><div>Move Camera</div>
              </div>
            </div> */}
          </div>
        )}
      </div>
    </div>
  );
};

export default CameraManager;